import { supabase } from '../utils/supabase';
import emailService from '../utils/emailService';
import { createLogger } from '../utils/logger.js';

const jobModerationLogger = createLogger('JobModerationService');

// Auto-approve threshold in hours (2 days)
const AUTO_APPROVE_THRESHOLD_HOURS = 48;

// Valid moderation statuses
const MODERATION_STATUSES = ['pending', 'approved', 'rejected', 'evaluation_needed'];

const jobModerationService = {
  // Check if a job was auto-approved (no human moderator)
  isAutoApproved(job) {
    return (
      job.moderation_status === 'approved' &&
      job.moderated_by === null &&
      job.moderation_comment?.includes('Auto-approved')
    );
  },

  // Check if a job is flagged for evaluation
  isEvaluationNeeded(job) {
    return job.moderation_status === 'evaluation_needed';
  },

  // Get content flags for a flagged job
  getContentFlags(job) {
    if (!job.content_flags) return null;
    return {
      obsceneWords: job.content_flags.obscene_words || [],
      spamIndicators: job.content_flags.spam_indicators || [],
      irrelevantContent: job.content_flags.irrelevant_content || [],
      totalFlags: job.content_flags.total_flags || 0,
      scannedAt: job.content_flags.scanned_at || null
    };
  },

  // Get time remaining until auto-approval for a pending job
  getAutoApproveInfo(job) {
    if (job.moderation_status !== 'pending') return null;
    const pendingSince = job.moderation_pending_since || job.created_at;
    if (!pendingSince) return null;

    const pendingDate = new Date(pendingSince);
    const autoApproveAt = new Date(pendingDate.getTime() + AUTO_APPROVE_THRESHOLD_HOURS * 60 * 60 * 1000);
    const now = new Date();
    const msRemaining = autoApproveAt.getTime() - now.getTime();

    return {
      pendingSince: pendingDate,
      autoApproveAt,
      hoursRemaining: Math.max(0, msRemaining / (1000 * 60 * 60)),
      isOverdue: msRemaining <= 0
    };
  },

  // Trigger auto-approve check manually (calls the Edge Function)
  async triggerAutoApprove(skipContentFilter = false) {
    try {
      const { data, error } = await supabase.functions.invoke('auto-approve-jobs', {
        body: { 
          threshold_hours: AUTO_APPROVE_THRESHOLD_HOURS,
          skip_content_filter: skipContentFilter
        }
      });

      if (error) throw error;

      jobModerationLogger.info('Manual auto-approve triggered', data);
      return { success: true, data };
    } catch (err) {
      jobModerationLogger.error('Error triggering auto-approve', err);
      return { success: false, error: err.message };
    }
  },
  // Get pending job moderations count (includes evaluation_needed)
  async getPendingJobModerationsCount() {
    try {
      // Use a simpler approach - get all jobs with employer data and filter in JS
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          moderation_status,
          employer_id,
          user_profiles!jobs_employer_id_fkey(subscription_tier)
        `)
        .in('moderation_status', ['pending', 'evaluation_needed']);

      if (error) throw error;

      // Filter for free tier employers
      const freeJobsCount = data?.filter(job => job.user_profiles?.subscription_tier === 'free').length || 0;

      return { success: true, count: freeJobsCount };
    } catch (err) {
      console.error('Error fetching pending job moderations count:', err);
      return { success: false, count: 0 };
    }
  },

  // Get all jobs for moderation with filters
  async getJobsForModeration(filter = 'pending') {
    try {
      let query = supabase
        .from('jobs')
        .select(`
          *,
          user_profiles!jobs_employer_id_fkey(
            id,
            full_name,
            company_name,
            email,
            subscription_tier
          )
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('moderation_status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter for free tier employers only and restructure data
      const filteredJobs = data?.filter(job => job.user_profiles?.subscription_tier === 'free').map(job => ({
        ...job,
        employer: job.user_profiles // Restructure to match expected format
      })) || [];

      return { success: true, data: filteredJobs };
    } catch (err) {
      console.error('Error fetching jobs for moderation:', err);
      return { success: false, error: err.message };
    }
  },

  // Moderate a job (approve/reject) - also handles evaluation_needed jobs
  async moderateJob(jobId, action, comment = '', moderatorId) {
    try {
      // Start a transaction-like approach for data consistency
      const moderationTimestamp = new Date().toISOString();
      
      // First, get the current job details with employer information
      const { data: currentJob, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          *,
          employer:user_profiles!jobs_employer_id_fkey(
            id, 
            full_name, 
            company_name, 
            email,
            subscription_tier
          )
        `)
        .eq('id', jobId)
        .single();

      if (fetchError || !currentJob) {
        throw new Error(`Failed to fetch job details: ${fetchError?.message || 'Job not found'}`);
      }

      // Validate the moderation action
      if (!['approved', 'rejected'].includes(action)) {
        throw new Error('Invalid moderation action. Must be "approved" or "rejected"');
      }

      // Validate source status - allow moderating pending and evaluation_needed jobs
      if (!['pending', 'evaluation_needed'].includes(currentJob.moderation_status)) {
        throw new Error(`Cannot moderate a job with status "${currentJob.moderation_status}". Only pending or evaluation_needed jobs can be moderated.`);
      }

      // Prepare update data
      const updateData = {
        moderation_status: action,
        moderated_by: moderatorId,
        moderated_at: moderationTimestamp,
        moderation_comment: comment || null,
        updated_at: moderationTimestamp
      };

      // Update job status based on moderation decision
      if (action === 'approved') {
        updateData.status = 'published'; // Make job visible to VAs
        updateData.content_flags = null; // Clear flags on approval
      } else if (action === 'rejected') {
        // Set status to draft for rejected jobs
        updateData.status = 'draft';
        updateData.content_flags = null; // Clear flags on rejection
      }

      // Update the job with moderation data
      const { data: updatedJob, error: updateError } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', jobId)
        .select(`
          *, 
          employer:user_profiles!jobs_employer_id_fkey(
            id, 
            full_name, 
            company_name, 
            email,
            subscription_tier
          )
        `)
        .single();

      if (updateError) {
        throw new Error(`Failed to update job: ${updateError.message}`);
      }

      jobModerationLogger.info(`Job ${jobId} ${action} by moderator ${moderatorId}`);

      // Create notification for employer
      const notificationData = {
        user_id: currentJob.employer_id,
        type: action === 'approved' ? 'job_approved' : 'job_rejected',
        title: `Job ${action === 'approved' ? 'Approved' : 'Rejected'}`,
        message: action === 'approved' 
          ? `Your job posting "${currentJob.title}" has been approved and is now live.`
          : `Your job posting "${currentJob.title}" was rejected.${comment ? ` Reason: ${comment}` : ''}`,
        data: { 
          job_id: jobId,
          job_title: currentJob.title,
          action: action,
          moderated_by: moderatorId,
          moderated_at: moderationTimestamp
        },
        is_read: false,
        created_at: moderationTimestamp
      };

      const { error: notificationError } = await supabase
        .from('notifications')
        .insert(notificationData);

      if (notificationError) {
        jobModerationLogger.error('Failed to create notification', notificationError);
        // Don't throw here - notification failure shouldn't fail the entire operation
      }

      // Send email notification to employer
      try {
        const employerEmail = currentJob.employer.email;
        const employerName = currentJob.employer.full_name;
        const companyName = currentJob.employer.company_name || employerName;

        if (action === 'approved') {
          await emailService.sendJobApprovalEmail(
            employerEmail,
            employerName,
            companyName,
            {
              title: currentJob.title,
              id: jobId,
              publishedAt: moderationTimestamp
            }
          );
          jobModerationLogger.info(`Job approval email sent to ${employerEmail}`);
        } else if (action === 'rejected') {
          await emailService.sendJobRejectionEmail(
            employerEmail,
            employerName,
            companyName,
            {
              title: currentJob.title,
              id: jobId
            },
            comment || 'No specific reason provided'
          );
          jobModerationLogger.info(`Job rejection email sent to ${employerEmail}`);
        }
      } catch (emailError) {
        jobModerationLogger.error('Failed to send email notification', emailError);
        // Email failure shouldn't fail the entire operation
      }

      // Log the moderation action for audit purposes
      try {
        await supabase
          .from('admin_actions')
          .insert({
            admin_user_id: moderatorId,
            action_type: `job_${action}`,
            target_job_id: jobId,
            reason: `Job moderation: ${action}`,
            notes: `Job: ${currentJob.title}. Employer: ${currentJob.employer?.full_name}. Comment: ${comment || 'None'}. Previous status: ${currentJob.moderation_status}. New status: ${action}`
          });
      } catch (auditError) {
        jobModerationLogger.error('Failed to log admin action', auditError);
        // Audit log failure shouldn't fail the entire operation
      }

      return { 
        success: true, 
        data: {
          ...updatedJob,
          employer: updatedJob.employer
        }
      };
    } catch (err) {
      jobModerationLogger.error('Error moderating job', err);
      return { 
        success: false, 
        error: err.message || 'Failed to moderate job'
      };
    }
  },

};

export { jobModerationService, AUTO_APPROVE_THRESHOLD_HOURS, MODERATION_STATUSES };
export default jobModerationService;